import { logger } from '../../lib/logger';
import { supabase } from '../../integrations/supabase/client';
import { ServiceResponse, IService } from '../service-registry';
import { clientService } from '../client.service';
import { notificationManager } from '../notifications/notification-manager.service';
import type { Opportunity, CreateOpportunityRequest, UpdateOpportunityRequest, OpportunityFilters, OpportunitySortOptions, PipelineAnalytics, OpportunityWithDetails, } from '@flowstate/types';
export interface IOpportunityService extends IService {
    getOpportunities(filters?: OpportunityFilters, sort?: OpportunitySortOptions): Promise<ServiceResponse<Opportunity[]>>;
    getOpportunity(id: string): Promise<ServiceResponse<Opportunity>>;
    createOpportunity(data: CreateOpportunityRequest): Promise<ServiceResponse<Opportunity>>;
    updateOpportunity(id: string, data: UpdateOpportunityRequest): Promise<ServiceResponse<Opportunity>>;
    deleteOpportunity(id: string): Promise<ServiceResponse<void>>;
    getOpportunitiesByClient(clientId: string): Promise<ServiceResponse<Opportunity[]>>;
    getOpportunitiesByStage(stage: Opportunity['stage']): Promise<ServiceResponse<Opportunity[]>>;
    getPipelineAnalytics(): Promise<ServiceResponse<PipelineAnalytics>>;
    updateOpportunityStage(id: string, stage: Opportunity['stage'], reason?: string): Promise<ServiceResponse<Opportunity>>;
    assignOpportunity(id: string, sales_rep_id: string): Promise<ServiceResponse<Opportunity>>;
    getOpportunityWithDetails(id: string): Promise<ServiceResponse<OpportunityWithDetails>>;
}
export class OpportunityService implements IOpportunityService {
    readonly name = "opportunity";
    async initialize(): Promise<void> {
        // no-op
    }
    async dispose(): Promise<void> {
        // no-op
    }
    // ============================================================================
    // CORE CRUD OPERATIONS
    // ============================================================================
    async getOpportunities(filters?: OpportunityFilters, sort?: OpportunitySortOptions): Promise<ServiceResponse<Opportunity[]>> {
        try {
            let query = supabase
                .from('opportunities')
                .select('*');
            // Apply filters
            if (filters) {
                if (filters.client_id) {
                    query = query.eq('client_id', filters.client_id);
                }
                if (filters.stage && filters.stage.length > 0) {
                    query = query.in('stage', filters.stage);
                }
                if (filters.sales_rep_id) {
                    query = query.eq('sales_rep_id', filters.sales_rep_id);
                }
                if (filters.source && filters.source.length > 0) {
                    query = query.in('source', filters.source);
                }
                if (filters.probability_min !== undefined) {
                    query = query.gte('probability', filters.probability_min);
                }
                if (filters.probability_max !== undefined) {
                    query = query.lte('probability', filters.probability_max);
                }
                if (filters.value_min !== undefined) {
                    query = query.gte('value', filters.value_min);
                }
                if (filters.value_max !== undefined) {
                    query = query.lte('value', filters.value_max);
                }
                if (filters.expected_close_date_from) {
                    query = query.gte('expected_close_date', filters.expected_close_date_from);
                }
                if (filters.expected_close_date_to) {
                    query = query.lte('expected_close_date', filters.expected_close_date_to);
                }
                if (filters.tags && filters.tags.length > 0) {
                    query = query.overlaps('tags', filters.tags);
                }
                if (filters.search) {
                    query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
                }
            }
            // Apply sorting
            if (sort) {
                query = query.order(sort.field, { ascending: sort.direction === 'asc' });
            }
            else {
                query = query.order('created_at', { ascending: false });
            }
            const { data, error } = await query;
            if (error) {
                return { success: false, error: `Failed to fetch opportunities: ${error.message}`, code: 'OPPORTUNITIES_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data || [] };
        }
        catch (error) {
            logger.error('OpportunityService.getOpportunities error:', error);
            return { success: false, error: `Unexpected error fetching opportunities: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITIES_FETCH_ERROR', service: this.name };
        }
    }
    async getOpportunity(id: string): Promise<ServiceResponse<Opportunity>> {
        try {
            const { data, error } = await supabase
                .from('opportunities')
                .select('*')
                .eq('id', id)
                .single();
            if (error) {
                return { success: false, error: `Failed to fetch opportunity: ${error.message}`, code: 'OPPORTUNITY_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data };
        }
        catch (error) {
            logger.error('OpportunityService.getOpportunity error:', error);
            return { success: false, error: `Unexpected error fetching opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_FETCH_ERROR', service: this.name };
        }
    }
    /**
     * @deprecated Browser writes to `opportunities` must go through the
     * TanStack DB collection path (`useOpportunities` /
     * `getOpportunitiesCollection`). Retained for server-side and
     * integration callers.
     */
    async createOpportunity(data: CreateOpportunityRequest): Promise<ServiceResponse<Opportunity>> {
        try {
            const { data: result, error } = await supabase
                .from('opportunities')
                .insert({
                ...data,
                created_by: (await supabase.auth.getUser()).data.user?.id,
                updated_by: (await supabase.auth.getUser()).data.user?.id,
            })
                .select()
                .single();
            if (error) {
                return { success: false, error: `Failed to create opportunity: ${error.message}`, code: 'OPPORTUNITY_CREATE_FAILED', service: this.name };
            }
            // 🚀 AUTO-LOG: Activity for client
            try {
                if (result.client_id) {
                    await clientService.createClientActivity({
                        client_id: result.client_id,
                        type: 'deal',
                        title: 'Opportunity Created',
                        description: `New opportunity: ${result.title} (Value: ${result.value})`,
                        related_deal_id: result.id
                    });
                }
            }
            catch (logError) {
                logger.warn("Failed to auto-log opportunity creation:", logError);
            }
            return { success: true, data: result };
        }
        catch (error) {
            logger.error('OpportunityService.createOpportunity error:', error);
            return { success: false, error: `Unexpected error creating opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_CREATE_ERROR', service: this.name };
        }
    }
    /**
     * @deprecated Browser writes to `opportunities` must go through the
     * TanStack DB collection path (`useOpportunities` /
     * `getOpportunitiesCollection`). Retained for server-side and
     * integration callers.
     */
    async updateOpportunity(id: string, data: UpdateOpportunityRequest): Promise<ServiceResponse<Opportunity>> {
        try {
            const { data: result, error } = await supabase
                .from('opportunities')
                .update({
                ...data,
                updated_by: (await supabase.auth.getUser()).data.user?.id,
            })
                .eq('id', id)
                .select()
                .single();
            if (error) {
                return { success: false, error: `Failed to update opportunity: ${error.message}`, code: 'OPPORTUNITY_UPDATE_FAILED', service: this.name };
            }
            return { success: true, data: result };
        }
        catch (error) {
            logger.error('OpportunityService.updateOpportunity error:', error);
            return { success: false, error: `Unexpected error updating opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_UPDATE_ERROR', service: this.name };
        }
    }
    /**
     * @deprecated Browser writes to `opportunities` must go through the
     * TanStack DB collection path (`useOpportunities` /
     * `getOpportunitiesCollection`). Retained for server-side and
     * integration callers.
     */
    async deleteOpportunity(id: string): Promise<ServiceResponse<void>> {
        try {
            const { error } = await supabase
                .from('opportunities')
                .delete()
                .eq('id', id);
            if (error) {
                return { success: false, error: `Failed to delete opportunity: ${error.message}`, code: 'OPPORTUNITY_DELETE_FAILED', service: this.name };
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            logger.error('OpportunityService.deleteOpportunity error:', error);
            return { success: false, error: `Unexpected error deleting opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_DELETE_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // SPECIALIZED QUERIES
    // ============================================================================
    async getOpportunitiesByClient(clientId: string): Promise<ServiceResponse<Opportunity[]>> {
        return this.getOpportunities({ client_id: clientId });
    }
    async getOpportunitiesByStage(stage: Opportunity['stage']): Promise<ServiceResponse<Opportunity[]>> {
        return this.getOpportunities({ stage: [stage] });
    }
    async getOpportunityWithDetails(id: string): Promise<ServiceResponse<OpportunityWithDetails>> {
        try {
            const { data, error } = await supabase
                .from('opportunities')
                .select(`
          *,
          client:clients(id, name, company, email, phone),
          contact_history:contact_history(id, contact_type, subject, created_at),
          documents:documents(id, name, type, status, created_at)
        `)
                .eq('id', id)
                .single();
            if (error) {
                return { success: false, error: `Failed to fetch opportunity details: ${error.message}`, code: 'OPPORTUNITY_DETAILS_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data as unknown as OpportunityWithDetails };
        }
        catch (error) {
            logger.error('OpportunityService.getOpportunityWithDetails error:', error);
            return { success: false, error: `Unexpected error fetching opportunity details: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_DETAILS_FETCH_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // PIPELINE MANAGEMENT
    // ============================================================================
    /**
     * @deprecated Browser writes to `opportunities` must go through the
     * TanStack DB collection path (`useUpdateOpportunityStage` in
     * `hooks/useOpportunities`). Retained for server-side and integration
     * callers.
     */
    async updateOpportunityStage(id: string, stage: Opportunity['stage'], reason?: string): Promise<ServiceResponse<Opportunity>> {
        try {
            const { data: result, error } = await supabase
                .from('opportunities')
                .update({
                stage,
                updated_by: (await supabase.auth.getUser()).data.user?.id,
                metadata: reason ? { stage_change_reason: reason } : undefined,
            })
                .eq('id', id)
                .select()
                .single();
            if (error) {
                return { success: false, error: `Failed to update opportunity stage: ${error.message}`, code: 'OPPORTUNITY_STAGE_UPDATE_FAILED', service: this.name };
            }
            // 🚀 AUTO-LOG: Activity for client
            try {
                if (result.client_id) {
                    await clientService.createClientActivity({
                        client_id: result.client_id,
                        type: 'deal',
                        title: 'Opportunity Stage Updated',
                        description: `Opportunity "${result.title}" stage changed to ${stage}${reason ? `. Reason: ${reason}` : ''}`,
                        related_deal_id: result.id
                    });
                }
            }
            catch (logError) {
                logger.warn("Failed to auto-log opportunity stage change:", logError);
            }
            // 📣 Send notification if stage changed
            try {
                const { data: authData } = await supabase.auth.getUser();
                if (result.sales_rep_id) {
                    await notificationManager.createNotification({
                        user_id: result.sales_rep_id,
                        type: 'CRM_DEAL_STAGE_CHANGED',
                        message_preview: `Opportunity "${result.title}" stage changed to ${stage}`,
                        entity_type: 'opportunity',
                        entity_id: result.id,
                        actor_id: authData.user?.id,
                        data: {
                            opportunity_title: result.title,
                            new_stage: stage,
                        },
                    });
                }
            }
            catch (notifError) {
                logger.warn("Failed to send opportunity stage change notification:", notifError);
            }
            return { success: true, data: result };
        }
        catch (error) {
            logger.error('OpportunityService.updateOpportunityStage error:', error);
            return { success: false, error: `Unexpected error updating opportunity stage: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_STAGE_UPDATE_ERROR', service: this.name };
        }
    }
    /**
     * @deprecated Browser writes to `opportunities` must go through the
     * TanStack DB collection path (`useAssignOpportunity` in
     * `hooks/useOpportunities`). Retained for server-side and integration
     * callers.
     */
    async assignOpportunity(id: string, sales_rep_id: string): Promise<ServiceResponse<Opportunity>> {
        try {
            const { data: result, error } = await supabase
                .from('opportunities')
                .update({
                sales_rep_id: sales_rep_id,
                updated_by: (await supabase.auth.getUser()).data.user?.id,
            })
                .eq('id', id)
                .select()
                .single();
            if (error) {
                return { success: false, error: `Failed to assign opportunity: ${error.message}`, code: 'OPPORTUNITY_ASSIGN_FAILED', service: this.name };
            }
            return { success: true, data: result };
        }
        catch (error) {
            logger.error('OpportunityService.assignOpportunity error:', error);
            return { success: false, error: `Unexpected error assigning opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'OPPORTUNITY_ASSIGN_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // ANALYTICS
    // ============================================================================
    async getPipelineAnalytics(): Promise<ServiceResponse<PipelineAnalytics>> {
        try {
            // Get all opportunities
            const opportunitiesResponse = await this.getOpportunities();
            if (opportunitiesResponse.success === false) {
                return { success: false, error: 'Failed to fetch opportunities for analytics', code: 'PIPELINE_ANALYTICS_FAILED', service: this.name };
            }
            const opportunities = opportunitiesResponse.data as Opportunity[];
            // Calculate basic metrics
            const totalOpportunities = opportunities.length;
            const totalValue = opportunities.reduce((sum: number, opp: Opportunity) => sum + opp.value, 0);
            const averageDealSize = totalOpportunities > 0 ? totalValue / totalOpportunities : 0;
            // Group by stage
            const opportunitiesByStage = opportunities.reduce((acc: Record<string, number>, opp: Opportunity) => {
                acc[opp.stage] = (acc[opp.stage] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);
            const valueByStage = opportunities.reduce((acc: Record<string, number>, opp: Opportunity) => {
                acc[opp.stage] = (acc[opp.stage] || 0) + opp.value;
                return acc;
            }, {} as Record<string, number>);
            // Calculate win/loss rates
            const wonOpportunities = opportunities.filter((opp: Opportunity) => opp.stage === 'closed-won').length;
            const lostOpportunities = opportunities.filter((opp: Opportunity) => opp.stage === 'closed-lost').length;
            const closedOpportunities = wonOpportunities + lostOpportunities;
            const winRate = closedOpportunities > 0 ? (wonOpportunities / closedOpportunities) * 100 : 0;
            const lossRate = closedOpportunities > 0 ? (lostOpportunities / closedOpportunities) * 100 : 0;
            // Calculate conversion rates between stages
            const conversionRates: Record<string, number> = {};
            const stages: Opportunity['stage'][] = ['lead', 'qualified', 'proposal', 'negotiation', 'closed-won'];
            for (let i = 0; i < stages.length - 1; i++) {
                const fromStage = stages[i];
                const toStage = stages[i + 1];
                const fromCount = opportunitiesByStage[fromStage] || 0;
                const toCount = opportunitiesByStage[toStage] || 0;
                conversionRates[`${fromStage}_to_${toStage}`] = fromCount > 0 ? (toCount / fromCount) * 100 : 0;
            }
            // Calculate average sales cycle (simplified)
            const closedOpps = opportunities.filter((opp: Opportunity) => opp.actual_close_date);
            const averageSalesCycle = closedOpps.length > 0
                ? closedOpps.reduce((sum: number, opp: Opportunity) => {
                    const created = new Date(opp.created_at);
                    const closed = new Date(opp.actual_close_date!);
                    return sum + (closed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
                }, 0) / closedOpps.length
                : 0;
            // Calculate pipeline velocity
            const pipelineVelocity = totalValue * (winRate / 100) / Math.max(averageSalesCycle, 1);
            // Get top performers (simplified)
            const topPerformers = opportunities
                .filter(opp => opp.sales_rep_id)
                .reduce((acc, opp) => {
                const repId = opp.sales_rep_id!;
                if (!acc[repId]) {
                    acc[repId] = {
                        user_id: repId,
                        user_name: `User ${repId.slice(0, 8)}`, // Simplified
                        opportunities_count: 0,
                        total_value: 0,
                        win_rate: 0,
                    };
                }
                acc[repId].opportunities_count++;
                acc[repId].total_value += opp.value;
                if (opp.stage === 'closed-won') {
                    acc[repId].win_rate++;
                }
                return acc;
            }, {} as Record<string, {
                user_id: string;
                user_name: string;
                opportunities_count: number;
                total_value: number;
                win_rate: number;
            }>);
            const topPerformersArray = Object.values(topPerformers).map((performer) => {
                const p = performer as {
                    user_id: string;
                    user_name: string;
                    opportunities_count: number;
                    total_value: number;
                    win_rate: number;
                };
                return {
                    ...p,
                    win_rate: p.opportunities_count > 0 ? (p.win_rate / p.opportunities_count) * 100 : 0,
                };
            }).sort((a, b) => b.total_value - a.total_value).slice(0, 5);
            // Group by month (trends)
            const monthlyTrends = opportunities.reduce((acc: Record<string, {
                month: string;
                opportunities: number;
                value: number;
                wins: number;
                losses: number;
            }>, opp: Opportunity) => {
                const month = new Date(opp.created_at).toISOString().slice(0, 7);
                if (!acc[month]) {
                    acc[month] = {
                        month,
                        opportunities: 0,
                        value: 0,
                        wins: 0,
                        losses: 0,
                    };
                }
                acc[month].opportunities++;
                acc[month].value += opp.value;
                if (opp.stage === 'closed-won')
                    acc[month].wins++;
                if (opp.stage === 'closed-lost')
                    acc[month].losses++;
                return acc;
            }, {} as Record<string, {
                month: string;
                opportunities: number;
                value: number;
                wins: number;
                losses: number;
            }>);
            const monthlyTrendsArray = Object.values(monthlyTrends)
                .sort((a, b) => a.month.localeCompare(b.month))
                .slice(-12);
            const analytics: PipelineAnalytics = {
                total_opportunities: totalOpportunities,
                total_value: totalValue,
                opportunities_by_stage: opportunitiesByStage,
                value_by_stage: valueByStage,
                conversion_rates: conversionRates,
                average_deal_size: averageDealSize,
                average_sales_cycle: averageSalesCycle,
                win_rate: winRate,
                loss_rate: lossRate,
                pipeline_velocity: pipelineVelocity,
                forecast_accuracy: 85, // Placeholder - would need historical data
                top_performers: topPerformersArray,
                monthly_trends: monthlyTrendsArray,
            };
            return { success: true, data: analytics };
        }
        catch (error) {
            logger.error('OpportunityService.getPipelineAnalytics error:', error);
            return { success: false, error: `Unexpected error calculating pipeline analytics: ${error instanceof Error ? error.message : 'Unknown error'}`, code: 'PIPELINE_ANALYTICS_ERROR', service: this.name };
        }
    }
}
export const opportunityService = new OpportunityService();
